/**
 * OTP staff sign-in — calls POST /api/auth/otp/request and /api/auth/otp/verify when deployed.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { portalApiPost, setPortalOtpToken } from '@/lib/portal-api';

type Step = 'phone' | 'otp';

export default function StaffOtpLoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');

  async function requestOtp(): Promise<void> {
    if (!phone.trim()) {
      setError('Please enter your WhatsApp number');
      return;
    }
    setError('');
    setRequesting(true);
    try {
      await portalApiPost<{ sent?: boolean }>('/auth/otp/request', {
        phone: phone.trim(),
        market: 'india',
      });
      setStep('otp');
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : 'OTP request failed (backend route may not be deployed yet).',
      );
    } finally {
      setRequesting(false);
    }
  }

  async function verifyOtp(): Promise<void> {
    if (otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }
    setError('');
    setVerifying(true);
    try {
      const data = await portalApiPost<{ token: string }>('/auth/otp/verify', {
        phone: phone.trim(),
        otp,
      });
      if (data.token) {
        setPortalOtpToken(data.token);
        navigate('/dashboard/home', { replace: true });
      } else {
        setError('No token returned from server');
      }
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : 'Verify failed (backend route may not be deployed yet).',
      );
    } finally {
      setVerifying(false);
    }
  }

  return (
    <main className="login-portal px-4">
      <Card>
        <CardHeader>
          <CardTitle>AdoraCare</CardTitle>
          <CardDescription>Clinic operating system — staff OTP sign-in</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {step === 'phone' ? (
            <>
              <label className="text-sm font-medium">WhatsApp number</label>
              <input
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                type="tel"
                placeholder="+91 98765 43210"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onKeyDown={(e) => void (e.key === 'Enter' && requestOtp())}
              />
              <Button type="button" disabled={requesting} onClick={() => void requestOtp()}>
                {requesting ? 'Sending OTP…' : 'Send OTP'}
              </Button>
              <p className="text-xs text-muted-foreground">OTP will be sent to your WhatsApp</p>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">OTP sent to {phone}</p>
              <label className="text-sm font-medium">Enter OTP</label>
              <input
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm tracking-widest"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="6-digit code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={(e) => void (e.key === 'Enter' && verifyOtp())}
              />
              <Button type="button" disabled={verifying} onClick={() => void verifyOtp()}>
                {verifying ? 'Verifying…' : 'Verify'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setStep('phone')}>
                Change number
              </Button>
            </>
          )}
          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
        </CardContent>
        <CardFooter className="flex flex-col gap-2 border-t">
          <p className="text-xs text-muted-foreground">
            OAuth for owners: use <Link to="/login">Log in</Link> in the main header.
          </p>
          <Link to="/home" className="text-sm text-primary underline-offset-4 hover:underline">
            Back to home
          </Link>
        </CardFooter>
      </Card>
    </main>
  );
}
